import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { useParams } from "react-router-dom"
import { useApi } from "../../hooks/ApiProvider"
import { MessageType, useMessageReport } from "../../components/MessageReporter"
import { PermissionSelector } from "../../components/Permissions/PermissionSelector"
import { resolveUserPermissions, useRole } from "../../components/RoleContext"

export const UserPermissions = () => {
    const { id } = useParams()
    const { consumerApi } = useApi()
    const { addMessage } = useMessageReport()
    const { reloadMe, me } = useRole()

    const [user, setUser] = useState()
    const [permissions, setPermissions] = useState()

    const resolvedUserPerms = useMemo(() => resolveUserPermissions(user), [user])
    const isDirty = useMemo(
        () => new Set(resolvedUserPerms).symmetricDifference(new Set(permissions)).size !== 0,
        [resolvedUserPerms, permissions]
    )

    const loadUser = useCallback(
        () =>
            consumerApi
                .getUser({ userId: id })
                .then((u) => {
                    setUser(u)
                    setPermissions(resolveUserPermissions(u))
                })
                .catch((e) => {
                    addMessage(
                        "Failed to load user",
                        "Something went wrong while loading the user, " +
                            "see the console for more details.",
                        MessageType.error,
                        e
                    )
                }),
        [consumerApi, id, addMessage]
    )

    useEffect(() => {
        loadUser()
    }, [loadUser])

    const updateUser = useCallback(
        () =>
            consumerApi
                .updateUser({
                    userId: id,
                    updateData: {
                        roleProperties: user.roleProperties,
                        allowedPermissions: Array.from(
                            new Set(permissions).difference(
                                new Set(user?.roleProperties?.permissions)
                            )
                        ),
                        deniedPermissions: Array.from(
                            new Set(user?.roleProperties?.permissions).difference(
                                new Set(permissions)
                            )
                        ),
                    },
                })
                .then(loadUser)
                .then(() => me.id === id && reloadMe())
                .then(() =>
                    addMessage(
                        "User updated",
                        "The user permissions have been updated successfully.",
                        MessageType.success
                    )
                )
                .catch((e) =>
                    addMessage(
                        "Failed to update user",
                        "Something went wrong while updating the user, " +
                            "see the console for more details.",
                        MessageType.error,
                        e
                    )
                ),
        [consumerApi, id, permissions, user, loadUser, me, reloadMe, addMessage]
    )

    return (
        <Row className="content-section">
            <Col>
                {!user ? (
                    <>
                        <h2>User permissions</h2>
                        <p>Loading...</p>
                    </>
                ) : (
                    <>
                        <h2>User permissions</h2>
                        <p>
                            You can only grant permissions you already have. Role defaults are only
                            for highlighting.
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <span className="border border-success text-success rounded px-2 py-1">
                                Added
                            </span>
                            <span className="border border-danger text-danger rounded px-2 py-1">
                                Removed
                            </span>
                        </div>
                        <div className="mb-3">
                            <button
                                className="btn btn-primary"
                                onClick={updateUser}
                                disabled={!isDirty}
                            >
                                Save
                            </button>
                        </div>
                        <PermissionSelector
                            activePermissions={permissions}
                            setPermissions={setPermissions}
                            defaultPermissions={user?.roleProperties?.permissions}
                        />
                    </>
                )}
            </Col>
        </Row>
    )
}
